import dayjs, { Dayjs } from 'dayjs';
import { PlaylistScreenOption } from '@modules/digital-signage/model/playlists.model.ts';
import {
  DeviceGroupScheduleModel,
  DeviceGroupScheduleReqModel
} from '@models/digital-signage/device-group-schedules.model.ts';
import { TimeRange } from '@models/time-range-picker.model.ts';
import {
  MinutesRange,
  ScheduleFormValues,
  ScheduleRuleFormValue
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import {
  WEEK_DAYS_API,
  isLocalSchedule
} from '@modules/digital-signage/utils/schedule.utils.ts';
import {
  minutesToDayjs,
  toMinutes
} from '@modules/digital-signage/utils/schedule-time.utils.ts';
import {
  getScheduleDates,
  sortDays,
  toDate
} from '@modules/digital-signage/utils/schedule-date.utils.ts';
import { getScheduleSlots } from '@modules/digital-signage/utils/schedule-slots.utils.ts';

export const createScheduleRule = (): ScheduleRuleFormValue => ({
  days: [],
  timeRanges: [{ start: null, end: null }]
});

export const replaceTimeRange = (
  timeRanges: TimeRange[],
  rangeIndex: number,
  { start, end }: MinutesRange
): TimeRange[] =>
  timeRanges.map((timeRange, index) =>
    index === rangeIndex
      ? { start: minutesToDayjs(start), end: minutesToDayjs(end) }
      : timeRange
  );

const getSchedulePlaylistId = ({
  playlist,
  schedules
}: Pick<DeviceGroupScheduleModel, 'playlist' | 'schedules'>):
  | string
  | null
  | undefined =>
  (playlist && typeof playlist !== 'string' ? playlist._id : playlist) ??
  schedules[0]?.playlists?.[0];

const getSchedulePlaylistOption = (
  schedule: DeviceGroupScheduleModel,
  playlistsOptions: PlaylistScreenOption[]
): PlaylistScreenOption | null => {
  const { playlist } = schedule;
  if (playlist && typeof playlist !== 'string') {
    return { id: playlist._id, title: playlist.name, value: playlist.name };
  }
  const playlistId = getSchedulePlaylistId(schedule);
  return playlistsOptions.find((option) => option.id === playlistId) ?? null;
};

export const mapScheduleToForm = (
  schedule: DeviceGroupScheduleModel | null,
  playlistsOptions: PlaylistScreenOption[]
): ScheduleFormValues => {
  if (!schedule) {
    return {
      name: '',
      playlist: null,
      startDate: null,
      endDate: null,
      rules: [createScheduleRule()]
    };
  }

  const { start, end } = getScheduleDates(schedule);

  const rules = new Map<string, ScheduleRuleFormValue>();
  getScheduleSlots(schedule).forEach(({ days, range }) => {
    const key = days.join();
    const rule = rules.get(key) ?? { days, timeRanges: [] };
    rule.timeRanges.push({
      start: minutesToDayjs(range.start),
      end: minutesToDayjs(range.end)
    });
    rules.set(key, rule);
  });

  return {
    name: schedule.name,
    playlist: getSchedulePlaylistOption(schedule, playlistsOptions),
    startDate: start ? dayjs(start) : null,
    endDate: end ? dayjs(end) : null,
    rules: rules.size ? [...rules.values()] : [createScheduleRule()]
  };
};

export const mapFormToScheduleReq = (
  values: ScheduleFormValues
): DeviceGroupScheduleReqModel => {
  const start = toDate(values.startDate);
  const end = toDate(values.endDate);

  return {
    name: values.name.trim(),
    playlist: values.playlist?.id ?? '',
    ...(start && { startDate: `${start}T00:00:00.000Z` }),
    ...(end && { endDate: `${end}T23:59:59.999Z` }),
    schedules: values.rules.flatMap((rule) =>
      [...rule.timeRanges]
        .sort(
          (a, b) => toMinutes(a.start as Dayjs) - toMinutes(b.start as Dayjs)
        )
        .map((range) => ({
          daysOfWeek: sortDays(rule.days).map((day) => WEEK_DAYS_API[day]),
          startTime: (range.start as Dayjs).format('HH:mm'),
          endTime: (range.end as Dayjs).format('HH:mm')
        }))
    )
  };
};

export const mapSchedulesToReq = (
  schedules: DeviceGroupScheduleModel[],
  withIds = true
): DeviceGroupScheduleReqModel[] =>
  schedules.map((schedule) => ({
    ...(withIds &&
      !isLocalSchedule(schedule) && {
        _id: schedule._id
      }),
    name: schedule.name,
    playlist: getSchedulePlaylistId(schedule) ?? '',
    ...(schedule.startDate && { startDate: schedule.startDate }),
    ...(schedule.endDate && { endDate: schedule.endDate }),
    schedules: schedule.schedules.map(({ daysOfWeek, startTime, endTime }) => ({
      daysOfWeek,
      startTime,
      endTime
    }))
  }));

const toComparableSchedules = (schedules: DeviceGroupScheduleModel[]): string =>
  JSON.stringify(
    mapSchedulesToReq(schedules).map(
      ({ startDate, endDate, schedules: slots, ...schedule }) => ({
        ...schedule,
        ...getScheduleDates({ startDate, endDate }),
        schedules: slots
          .map(
            ({ daysOfWeek, startTime, endTime }) =>
              `${[...daysOfWeek].sort().join()}|${startTime}|${endTime}`
          )
          .sort()
      })
    )
  );

export const hasScheduleChanges = (
  schedules: DeviceGroupScheduleModel[],
  savedSchedules: DeviceGroupScheduleModel[]
): boolean =>
  toComparableSchedules(schedules) !== toComparableSchedules(savedSchedules);
